import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getImages, getVideos, getMusic } from '../services/contentService';

// ─── Design tokens (exact match to DashboardPage) ───────────────
const T = {
  bg:          '#ffffff',
  card:        '#ffffff',
  purple:      '#7c3aed',
  purpleMid:   '#6d28d9',
  purpleLight: '#ede9fe',
  purpleFaint: '#f5f3ff',
  textPrimary: '#1e1b4b',
  textSecond:  '#4c4780',
  textMuted:   '#8b8fad',
  border:      'rgba(124,58,237,0.1)',
  shadow:      '8px 8px 18px #cccce6, -8px -8px 18px #ffffff',
  shadowSm:    '5px 5px 12px #cccce6, -5px -5px 12px #ffffff',
  shadowIn:    'inset 3px 3px 7px #d0d0ec, inset -3px -3px 7px rgba(255,255,255,0.85)',
};

// ─── Helper: get best URL from a content item ────────────────────
const getUrl = (item) => item?.publicUrl || item?.url || item?.public_url || item?.r2_url || null;

// ─── Card wrapper ────────────────────────────────────────────────
function NmCard({ children, onClick, style = {} }) {
  return (
    <motion.div
      onClick={onClick}
      whileHover={onClick ? { y: -4, boxShadow: T.shadow } : {}}
      transition={{ duration: 0.18 }}
      style={{
        background: T.card,
        borderRadius: 18,
        boxShadow: T.shadowSm,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </motion.div>
  );
}

// ─── Filter tab pills ────────────────────────────────────────────
function TabPill({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 20px', borderRadius: 100, border: 'none', cursor: 'pointer',
      background: active ? `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})` : T.card,
      boxShadow: active ? `0 4px 14px rgba(124,58,237,0.28)` : T.shadowSm,
      color: active ? '#fff' : T.textSecond,
      fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
      transition: 'all 0.18s', whiteSpace: 'nowrap',
    }}>
      {children}
    </button>
  );
}

// ─── Image card ──────────────────────────────────────────────────
function ImageCard({ item, onOpen }) {
  const [state, setState] = useState('loading'); // loading | ok | err
  const url = getUrl(item);

  return (
    <NmCard onClick={() => onOpen({ ...item, _type: 'image' })}>
      {/* Thumbnail */}
      <div style={{ aspectRatio: '1/1', background: T.purpleLight, position: 'relative', overflow: 'hidden' }}>
        {url ? (
          <>
            {state === 'loading' && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LoadDots />
              </div>
            )}
            <img
              src={url}
              alt={item.prompt || ''}
              onLoad={() => setState('ok')}
              onError={() => setState('err')}
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: state === 'ok' ? 1 : 0, transition: 'opacity 0.3s' }}
            />
            {state === 'err' && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <span style={{ fontSize: 28 }}>🖼️</span>
                <span style={{ fontSize: 10, color: T.textMuted }}>Preview unavailable</span>
              </div>
            )}
          </>
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <span style={{ fontSize: 28 }}>🖼️</span>
            <span style={{ fontSize: 10, color: T.textMuted }}>No URL</span>
          </div>
        )}
        {/* Type badge */}
        <span style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(124,58,237,0.85)', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 9, fontWeight: 800, letterSpacing: 0.5 }}>
          IMAGE
        </span>
      </div>
      {/* Info */}
      <div style={{ padding: '10px 12px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: T.purple, marginBottom: 3 }}>{item.model || 'AI Model'}</div>
        <div style={{ fontSize: 11, color: T.textSecond, lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {item.prompt || 'Generated image'}
        </div>
      </div>
    </NmCard>
  );
}

// ─── Video card ──────────────────────────────────────────────────
function VideoCard({ item, onOpen }) {
  const url = getUrl(item);
  const videoRef = useRef(null);
  const [hover, setHover] = useState(false);

  return (
    <NmCard
      onClick={() => onOpen({ ...item, _type: 'video' })}
      style={{ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) }}
    >
      <div
        style={{ aspectRatio: '16/9', background: '#1a1030', position: 'relative', overflow: 'hidden' }}
        onMouseEnter={() => { setHover(true); videoRef.current?.play(); }}
        onMouseLeave={() => { setHover(false); if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; } }}
      >
        {url ? (
          <video ref={videoRef} src={url} muted loop playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <span style={{ fontSize: 32 }}>🎬</span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>Processing…</span>
          </div>
        )}
        {/* Overlay play icon (hidden on hover) */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: hover ? 0 : 1, transition: 'opacity 0.2s' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(124,58,237,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontSize: 16, marginLeft: 2 }}>▶</span>
          </div>
        </div>
        <span style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(79,70,229,0.9)', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 9, fontWeight: 800, letterSpacing: 0.5 }}>
          VIDEO
        </span>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#6d28d9', marginBottom: 3 }}>{item.model || 'AI Model'}</div>
        <div style={{ fontSize: 11, color: T.textSecond, lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {item.prompt || 'Generated video'}
        </div>
      </div>
    </NmCard>
  );
}

// ─── Music card ──────────────────────────────────────────────────
function MusicCard({ item, onOpen, activePlaying, setActivePlaying }) {
  const url = getUrl(item);
  const audioRef = useRef(null);
  const isPlaying = activePlaying === item.id;

  const toggle = (e) => {
    e.stopPropagation();
    if (isPlaying) {
      audioRef.current?.pause();
      setActivePlaying(null);
    } else {
      // Pause any currently playing audio by triggering a custom event
      document.querySelectorAll('audio').forEach(a => a.pause());
      audioRef.current?.play();
      setActivePlaying(item.id);
    }
  };

  const bars = [35, 60, 45, 75, 40, 80, 55, 70, 45, 65, 78, 50];

  return (
    <NmCard onClick={() => onOpen({ ...item, _type: 'music' })}>
      <div style={{ height: 110, background: `linear-gradient(135deg, #3b1d8a, #5b21b6)`, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Animated bars */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {bars.map((h, i) => (
            <motion.div key={i}
              animate={isPlaying ? { scaleY: [0.4, h / 40, 0.3, h / 50, 0.6] } : { scaleY: 0.4 }}
              transition={isPlaying ? { repeat: Infinity, duration: 0.7 + i * 0.06, ease: 'easeInOut' } : { duration: 0.3 }}
              style={{ width: 4, height: h * 0.9, background: 'rgba(196,181,253,0.7)', borderRadius: 2, transformOrigin: 'bottom' }}
            />
          ))}
        </div>
        {/* Play button */}
        <button onClick={toggle} style={{
          position: 'absolute', right: 10, bottom: 10, width: 32, height: 32,
          borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: isPlaying ? T.purple : 'rgba(255,255,255,0.15)',
          backdropFilter: 'blur(4px)', color: '#fff', fontSize: 13,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
        }}>
          {isPlaying ? '⏸' : '▶'}
        </button>
        {item.duration && (
          <span style={{ position: 'absolute', left: 8, top: 8, background: 'rgba(0,0,0,0.45)', color: '#fff', borderRadius: 5, padding: '2px 6px', fontSize: 9, fontWeight: 700 }}>
            {Math.floor(item.duration / 60)}:{String(item.duration % 60).padStart(2, '0')}
          </span>
        )}
        <span style={{ position: 'absolute', top: 8, left: item.duration ? 48 : 8, background: 'rgba(139,92,246,0.85)', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 9, fontWeight: 800, letterSpacing: 0.5 }}>
          MUSIC
        </span>
        {url && <audio ref={audioRef} src={url} onEnded={() => setActivePlaying(null)} />}
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#8b5cf6', marginBottom: 3 }}>{item.model || 'Suno'}</div>
        <div style={{ fontSize: 11, color: T.textSecond, lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {item.prompt || item.title || 'Generated track'}
        </div>
      </div>
    </NmCard>
  );
}

// ─── Loading dots ────────────────────────────────────────────────
function LoadDots() {
  return (
    <div style={{ display: 'flex', gap: 5 }}>
      {[0, 0.15, 0.3].map((d, i) => (
        <motion.div key={i}
          animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 0.9, delay: d }}
          style={{ width: 6, height: 6, borderRadius: '50%', background: T.purple }}
        />
      ))}
    </div>
  );
}

// ─── Lightbox ────────────────────────────────────────────────────
function Lightbox({ item, onClose }) {
  const url = getUrl(item);
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,10,40,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        style={{ background: '#f8f6ff', borderRadius: 24, overflow: 'hidden', maxWidth: 860, width: '100%', boxShadow: '0 40px 80px rgba(0,0,0,0.4)' }}
      >
        {/* Media area */}
        <div style={{ background: '#13092a', minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {item._type === 'image' && url && <img src={url} alt={item.prompt} style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain' }} />}
          {item._type === 'video' && url && <video src={url} controls autoPlay style={{ maxWidth: '100%', maxHeight: '65vh' }} />}
          {item._type === 'music' && (
            <div style={{ width: '100%', padding: '40px 32px', textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 20 }}>🎵</div>
              {url && <audio controls src={url} autoPlay style={{ width: '100%', maxWidth: 480 }} />}
            </div>
          )}
          {!url && <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>No preview available</div>}
        </div>
        {/* Info + actions */}
        <div style={{ padding: '18px 24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: T.purple, textTransform: 'uppercase', marginBottom: 5 }}>
              {item._type} · {item.model || 'AI'}
            </div>
            <div style={{ fontSize: 13, color: T.textSecond, lineHeight: 1.55 }}>{item.prompt || item.title || '-'}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {url && (
              <a href={url} download target="_blank" rel="noreferrer" style={{ padding: '8px 16px', borderRadius: 10, background: `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})`, color: '#fff', fontSize: 11, fontWeight: 700, textDecoration: 'none', boxShadow: `0 4px 12px rgba(124,58,237,0.3)` }}>
                ⬇ Download
              </a>
            )}
            <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 10, background: T.purpleLight, color: T.purple, fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
              ✕ Close
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Stat pill ───────────────────────────────────────────────────
function StatPill({ emoji, label, count, color }) {
  return (
    <div style={{ background: T.card, borderRadius: 16, padding: '16px 20px', boxShadow: T.shadowSm, display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: 13, background: T.purpleLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{emoji}</div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color, letterSpacing: -0.5, lineHeight: 1 }}>{count}</div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>{label}</div>
      </div>
    </div>
  );
}

const TABS = [
  { id: 'all',   label: 'All creations' },
  { id: 'image', label: '🖼️ Images'     },
  { id: 'video', label: '🎬 Videos'     },
  { id: 'music', label: '🎵 Music'      },
];

// ═══════════════════════════════════════════════════════════════
// GALLERY SECTION
// ═══════════════════════════════════════════════════════════════
export function GallerySection({ userId }) {
  const [tab,           setTab]           = useState('all');
  const [images,        setImages]        = useState([]);
  const [videos,        setVideos]        = useState([]);
  const [music,         setMusic]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [lightboxItem,  setLightboxItem]  = useState(null);
  const [activePlaying, setActivePlaying] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const [imgs, vids, musicResponse] = await Promise.all([
      getImages(userId).catch(() => []),
      getVideos(userId).catch(() => []),
      getMusic(userId).catch(() => ({ tracks: [] })),
    ]);
    const musicArray = musicResponse?.tracks || (Array.isArray(musicResponse) ? musicResponse : []);

    setImages((imgs || []).map(i => ({ ...i, _type: 'image' })));
    setVideos((vids || []).map(v => ({ ...v, _type: 'video' })));
    setMusic(musicArray.map(m => ({ ...m, _type: 'music' })));
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // All items sorted newest-first
  const allItems = [
    ...images.map(i => ({ ...i, _sort: i.created_at })),
    ...videos.map(v => ({ ...v, _sort: v.created_at })),
    ...music.map(m => ({ ...m, _sort: m.created_at })),
  ].sort((a, b) => (b._sort || 0) - (a._sort || 0));

  const displayed = tab === 'all' ? allItems
    : tab === 'image' ? images
    : tab === 'video' ? videos
    : music;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Header card ── */}
      <div style={{ background: T.card, borderRadius: 20, padding: '24px 28px', boxShadow: T.shadowSm }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.4 }}>My Gallery</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 3 }}>
              {loading ? 'Loading…' : `${allItems.length} creation${allItems.length !== 1 ? 's' : ''} across all generators`}
            </div>
          </div>
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 12, border: 'none', background: T.purpleLight, color: T.purple, fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: T.shadowSm }}>
            ↺ Refresh
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
          <StatPill emoji="🖼️" label="Images generated" count={images.length} color={T.purple} />
          <StatPill emoji="🎬" label="Videos generated" count={videos.length} color="#4f46e5" />
          <StatPill emoji="🎵" label="Music tracks"     count={music.length}  color="#8b5cf6" />
        </div>
      </div>

      {/* ── Tab row ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <TabPill key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}
            <span style={{
              marginLeft: 7, background: tab === t.id ? 'rgba(255,255,255,0.22)' : T.purpleLight,
              color: tab === t.id ? '#fff' : T.purple,
              borderRadius: 100, padding: '1px 7px', fontSize: 10, fontWeight: 800,
            }}>
              {t.id === 'all' ? allItems.length : t.id === 'image' ? images.length : t.id === 'video' ? videos.length : music.length}
            </span>
          </TabPill>
        ))}
      </div>

      {/* ── Grid ── */}
      <div style={{ background: T.card, borderRadius: 20, padding: 24, boxShadow: T.shadowSm, minHeight: 260 }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '80px 0', color: T.textMuted }}>
            <LoadDots />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Loading your creations…</span>
          </div>
        ) : displayed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '70px 0' }}>
            <div style={{ fontSize: 46, marginBottom: 14 }}>
              {tab === 'image' ? '🖼️' : tab === 'video' ? '🎬' : tab === 'music' ? '🎵' : '✨'}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, marginBottom: 6 }}>
              {tab === 'all' ? 'No creations yet' : `No ${tab}s generated yet`}
            </div>
            <div style={{ fontSize: 12, color: T.textMuted }}>
              {tab === 'all' ? 'Generate something in any workspace to see it here.' : `Head to the ${tab.charAt(0).toUpperCase()}${tab.slice(1)} Generator to create your first ${tab}.`}
            </div>
          </div>
        ) : (
          <motion.div
            layout
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}
          >
            <AnimatePresence>
              {displayed.map(item => {
                const key = `${item._type}-${item.id || Math.random()}`;
                if (item._type === 'image') return <ImageCard key={key} item={item} onOpen={setLightboxItem} />;
                if (item._type === 'video') return <VideoCard key={key} item={item} onOpen={setLightboxItem} />;
                if (item._type === 'music') return <MusicCard key={key} item={item} onOpen={setLightboxItem} activePlaying={activePlaying} setActivePlaying={setActivePlaying} />;
                return null;
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxItem && <Lightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />}
      </AnimatePresence>
    </div>
  );
}
